import { z } from "zod";
import { useMemo } from "react";
import { cn } from "@/shared/lib/utils";
import { DataTable, DataTableSkeleton } from "@/shared/components";
import type { ColumnDef } from "@tanstack/react-table";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const AgentDataTableSchema = z.object({
  columns: z.array(
    z.object({
      key: z.string(),
      header: z.string(),
      align: z.enum(["left", "center", "right"]).optional(),
    }),
  ),
  rows: z.array(z.record(z.string(), z.unknown())),
});

type AgentDataTableData = z.infer<typeof AgentDataTableSchema>;

/* ────────────────────── Component ────────────────────── */

export function AgentDataTable({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<AgentDataTableData>) {
  const { columns: colDefs, rows } = widget.data;

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(
    () =>
      colDefs.map((col) => ({
        id: col.key,
        accessorKey: col.key,
        header: col.header,
        cell: ({ getValue }) => {
          const val = getValue();
          if (val == null) return <span className="text-text-tertiary">—</span>;
          const str = String(val);
          return (
            <span
              className={cn(
                "text-[12px]",
                col.align === "right" && "block text-right tabular-nums",
                col.align === "center" && "block text-center",
              )}
            >
              {str}
            </span>
          );
        },
      })),
    [colDefs],
  );

  return (
    <div
      className={cn(
        "flex flex-col gap-2",
        widget.deepLink && onDeepLink && "relative",
        className,
      )}
    >
      <DataTable
        columns={columns}
        data={rows}
        exportFilename={widget.title ?? "agent-export"}
      />
      {widget.deepLink && onDeepLink && (
        <div className="flex justify-end">
          <button
            onClick={() => onDeepLink(widget.deepLink!)}
            className="text-[12px] font-medium text-text-brand hover:underline"
          >
            자세히 보기 →
          </button>
        </div>
      )}
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function AgentDataTableSkeleton({ className }: { className?: string }) {
  return <DataTableSkeleton columns={5} rows={6} className={className} />;
}

/* ────────────────────── Widget Definition ────────────────────── */

const agentDataTableWidget = defineWidget<AgentDataTableData>({
  type: WIDGET_TYPES.DATA_TABLE,
  label: "데이터 테이블",
  schema: AgentDataTableSchema,
  component: AgentDataTable,
  skeleton: AgentDataTableSkeleton,
  defaultLayout: "full",
  maxHeight: 400,
  aiHint: "구조화된 테이블 데이터 (키워드 목록, 채널 목록, 검색 결과 등)",
});

registerWidget(agentDataTableWidget);
